/**
 * Edit form for a single rule (PRP-004).
 *
 * Hosts inline in the RuleDetail drawer when the user clicks "Edit" and has
 * editor / expert / super_admin role. Submit calls RulesAPI.update and
 * surfaces the result back via onSaved. Cancel calls onCancel.
 *
 * JSON editors are textareas with monospace font + parse-on-keystroke
 * validation; Save is disabled while either conditions or actions has a
 * parse error (PRP §5 fallback when no monaco editor is available).
 */
import * as React from 'react';
import { Loader2 } from 'lucide-react';

import { RulesAPI, type RuleUpdatePayload } from '../../lib/rulesApi';
import { getErrorMessage } from '../../lib/utils';
import type { RuleDetail, RuleSeverity, RuleStatus } from '../../types/rules';

interface RuleEditFormProps {
  rule: RuleDetail;
  onCancel: () => void;
  onSaved: (next: RuleDetail) => void;
}

interface FormState {
  name: string;
  description: string;
  severity: RuleSeverity;
  status: RuleStatus;
  domains: string;
  tags: string;
  conditionsText: string;
  actionsText: string;
  changeSummary: string;
}

function csvToList(value: string): string[] {
  return value
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
}

function listToCsv(values: string[] | undefined): string {
  return (values ?? []).join(', ');
}

function stableJson(value: unknown): string {
  return JSON.stringify(value ?? null, null, 2);
}

function parseJson(text: string): { value: unknown; error: string | null } {
  const trimmed = text.trim();
  if (!trimmed) return { value: null, error: 'Required' };
  try {
    return { value: JSON.parse(trimmed), error: null };
  } catch (err) {
    return { value: null, error: getErrorMessage(err) };
  }
}

function fromRule(rule: RuleDetail): FormState {
  return {
    name: rule.name,
    description: rule.description ?? '',
    severity: rule.severity,
    status: rule.status,
    domains: listToCsv(rule.domains),
    tags: listToCsv(rule.tags),
    conditionsText: stableJson(rule.conditions ?? {}),
    actionsText: stableJson(rule.actions ?? []),
    changeSummary: '',
  };
}

const RuleEditForm: React.FC<RuleEditFormProps> = ({ rule, onCancel, onSaved }) => {
  const [form, setForm] = React.useState<FormState>(() => fromRule(rule));
  const [submitting, setSubmitting] = React.useState(false);
  const [serverError, setServerError] = React.useState<string | null>(null);

  // Re-seed when the underlying rule changes (e.g. after a save returns
  // the new version and the parent re-mounts us with fresh state).
  React.useEffect(() => {
    setForm(fromRule(rule));
    setServerError(null);
  }, [rule]);

  const conditionsParse = React.useMemo(
    () => parseJson(form.conditionsText),
    [form.conditionsText],
  );
  const actionsParse = React.useMemo(
    () => parseJson(form.actionsText),
    [form.actionsText],
  );

  const jsonValid = conditionsParse.error === null && actionsParse.error === null;

  const update = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!jsonValid || submitting) return;

    // Build a diff payload: only send fields that changed vs the original.
    const payload: RuleUpdatePayload = {};
    if (form.name.trim() !== rule.name) payload.name = form.name.trim();
    if (form.description !== (rule.description ?? '')) payload.description = form.description;
    if (form.severity !== rule.severity) payload.severity = form.severity;
    if (form.status !== rule.status) payload.status = form.status;

    const nextDomains = csvToList(form.domains);
    if (JSON.stringify(nextDomains) !== JSON.stringify(rule.domains ?? []))
      payload.domains = nextDomains;

    const nextTags = csvToList(form.tags);
    if (JSON.stringify(nextTags) !== JSON.stringify(rule.tags ?? []))
      payload.tags = nextTags;

    // Always parsed in this branch (jsonValid guards above).
    const conditions = conditionsParse.value as Record<string, unknown> | null;
    const actions = actionsParse.value as Array<Record<string, unknown>> | null;
    if (JSON.stringify(conditions) !== JSON.stringify(rule.conditions ?? {}))
      payload.conditions = (conditions ?? {}) as Record<string, unknown>;
    if (JSON.stringify(actions) !== JSON.stringify(rule.actions ?? []))
      payload.actions = (actions ?? []) as Array<Record<string, unknown>>;

    if (Object.keys(payload).length === 0) {
      setServerError('No changes to save.');
      return;
    }

    if (form.changeSummary.trim()) payload.change_summary = form.changeSummary.trim();

    setSubmitting(true);
    setServerError(null);
    try {
      const updated = await RulesAPI.update(rule.id, payload);
      onSaved(updated);
    } catch (err) {
      setServerError(getErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4">
      {serverError && (
        <div className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-800">
          {serverError}
        </div>
      )}

      <div className="grid grid-cols-1 gap-3">
        <LabeledField label="Name" required>
          <input
            type="text"
            required
            value={form.name}
            onChange={(e) => update('name', e.target.value)}
            className="w-full rounded border border-slate-300 px-2 py-1.5 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          />
        </LabeledField>

        <LabeledField label="Description">
          <textarea
            rows={2}
            value={form.description}
            onChange={(e) => update('description', e.target.value)}
            className="w-full rounded border border-slate-300 px-2 py-1.5 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          />
        </LabeledField>

        <div className="grid grid-cols-2 gap-3">
          <LabeledField label="Severity">
            <select
              value={form.severity}
              onChange={(e) => update('severity', e.target.value as RuleSeverity)}
              className="w-full rounded border border-slate-300 px-2 py-1.5 text-sm"
            >
              <option value="must">must</option>
              <option value="should">should</option>
              <option value="suggested">suggested</option>
            </select>
          </LabeledField>
          <LabeledField label="Status">
            <select
              value={form.status}
              onChange={(e) => update('status', e.target.value as RuleStatus)}
              className="w-full rounded border border-slate-300 px-2 py-1.5 text-sm"
            >
              <option value="draft">draft</option>
              <option value="proposed">proposed</option>
              <option value="active">active</option>
              <option value="retired">retired</option>
            </select>
          </LabeledField>
        </div>

        <LabeledField label="Domains" hint="Comma-separated">
          <input
            type="text"
            value={form.domains}
            onChange={(e) => update('domains', e.target.value)}
            className="w-full rounded border border-slate-300 px-2 py-1.5 text-sm font-mono"
          />
        </LabeledField>

        <LabeledField label="Tags" hint="Comma-separated">
          <input
            type="text"
            value={form.tags}
            onChange={(e) => update('tags', e.target.value)}
            className="w-full rounded border border-slate-300 px-2 py-1.5 text-sm font-mono"
          />
        </LabeledField>
      </div>

      <JsonField
        label="Conditions (JSON)"
        value={form.conditionsText}
        onChange={(v) => update('conditionsText', v)}
        error={conditionsParse.error}
      />

      <JsonField
        label="Actions (JSON array)"
        value={form.actionsText}
        onChange={(v) => update('actionsText', v)}
        error={actionsParse.error}
      />

      <LabeledField label="Change summary" hint="Optional — shows in version history">
        <input
          type="text"
          value={form.changeSummary}
          onChange={(e) => update('changeSummary', e.target.value)}
          placeholder="e.g. Fixed missing citation"
          className="w-full rounded border border-slate-300 px-2 py-1.5 text-sm"
        />
      </LabeledField>

      <footer className="flex items-center justify-end gap-2 border-t pt-3">
        <button
          type="button"
          onClick={onCancel}
          disabled={submitting}
          className="rounded border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-60"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={!jsonValid || submitting}
          className="inline-flex items-center gap-2 rounded bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-60"
        >
          {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
          Save
        </button>
      </footer>
    </form>
  );
};

const LabeledField: React.FC<{
  label: string;
  hint?: string;
  required?: boolean;
  children: React.ReactNode;
}> = ({ label, hint, required, children }) => (
  <label className="block text-sm">
    <span className="mb-1 flex items-baseline justify-between">
      <span className="font-medium text-slate-700">
        {label}
        {required && <span className="ml-0.5 text-red-600">*</span>}
      </span>
      {hint && <span className="text-xs text-muted-foreground">{hint}</span>}
    </span>
    {children}
  </label>
);

const JsonField: React.FC<{
  label: string;
  value: string;
  onChange: (v: string) => void;
  error: string | null;
}> = ({ label, value, onChange, error }) => (
  <div className="space-y-1">
    <div className="flex items-baseline justify-between">
      <span className="text-sm font-medium text-slate-700">{label}</span>
      <span
        className={
          error ? 'text-xs font-medium text-red-700' : 'text-xs font-medium text-emerald-700'
        }
      >
        {error ? `Invalid: ${error}` : 'Valid JSON'}
      </span>
    </div>
    <textarea
      rows={10}
      spellCheck={false}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className={`w-full rounded border px-2 py-1.5 font-mono text-xs leading-relaxed ${
        error ? 'border-red-400 bg-red-50' : 'border-slate-300 bg-slate-50'
      }`}
    />
  </div>
);

export default RuleEditForm;
